import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, Observable, catchError, finalize, throwError } from 'rxjs';
import { LocalStorageService } from '../../../common/services/local-storage.service';
import { Log } from '../../../core/models';
import {
  AUTHENTICATION_SERVICE,
  AuthenticationService,
} from '../../../core/services';
import { RouteStateService } from '../../../core/services/route-state.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { isNullOrEmpty } from '../../../core/utils';
import { ErrorService } from './error.service';

/**
 * Http error interceptor
 *
 * @export
 * @class HttpErrorInterceptor
 * @implements {HttpInterceptor}
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  // 判斷是否為第一個 401 error
  private first401Error: boolean = true;
  constructor(
    private logger: NGXLogger,
    private errorService: ErrorService,
    private localStroageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private routeStateService: RouteStateService,
    @Inject(AUTHENTICATION_SERVICE)
    private authenticationService: AuthenticationService
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.apimFilter(request.url)) {
      return next.handle(request).pipe(
        catchError((err: any) => {
          this.logger.error(err);
          if (request.method === 'POST' && request.body instanceof Log) {
            return EMPTY;
          }
          switch (err.status) {
            case 400:
              const errorHeader = err.headers.get('X-Error-Code');
              this.logger.info('businessError', err, errorHeader);
              if (isNullOrEmpty(errorHeader) === false) {
                const errBody = err.error ?? {};
                errBody.errorCode = errorHeader;
                this.logger.info(
                  'businessError-handle',
                  err?.error.errorCode,
                  err?.error.detail
                );
                let isHandle = true;
                this.errorService
                  .handleBusinessError(err?.error.errorCode!, err?.error.detail)
                  .then((result) => {
                    isHandle = result;
                  });

                errBody.isHandle = isHandle;

                return throwError(errBody);
              }
              break;
            case 401:
              if (
                ['RetailWebSubmission'].includes(
                  globalThis.sigv.appContext.projectConfig.systemCode
                )
              ) {
                this.sessionStorageService.removeItem('dealerUserProfile');
                this.localStroageService.removeItem('userProfile');
                this.routeStateService.navigateTo('/login');
              } else if (this.first401Error) {
                this.first401Error = false;
                this.authenticationService
                  .refreshTokenForNoPermission$()
                  .subscribe((res) => {
                    if (res) {
                      this.first401Error = true;
                    }
                  });
              }
              break;
            default:
              this.handleApiError(request, err);
              break;
          }
          return EMPTY;
        }),
        finalize(() => {})
      );
    } else {
      return next.handle(request).pipe(
        catchError((err: HttpErrorResponse) => {
          this.logger.error(err);
          if (err.status === 401) {
            this.handleApiError(request, err);
            return EMPTY;
          }
          // 外部系統 API 回傳 500 的情況
          if (err.status === 500 || err.status === 404) {
            if (request.method === 'POST' && request.body instanceof Log) {
              return EMPTY;
            }
            this.handleApiError(request, err);
            return EMPTY;
          }
          return throwError(err);
        }),
        finalize(() => {})
      );
    }
  }

  private apimFilter(uri: string): boolean {
    let hit = false;
    hit = globalThis.sigv.appContext.envConfig.apimFilters.some((apimFilter) =>
      uri.includes(apimFilter)
    );
    return hit;
  }

  private handleApiError(
    req: HttpRequest<unknown>,
    err: HttpErrorResponse
  ): void {
    const serverTraceId = err.headers.get('X-Server-TraceId');
    const clientTraceId = req.headers.get('X-Client-TraceId');
    const errMsg = `${err.message}, { X-Server-TraceId: ${
      serverTraceId ?? ''
    }, X-Client-TraceId: ${clientTraceId ?? ''} }`;
    this.logger.error('error log', req, err, clientTraceId, serverTraceId);
    this.errorService.handleApiError(new Error(errMsg), clientTraceId!);
  }
}
